import { getAgentsStreamBaseUrl, getValidClaimIntakeBearerToken } from '@circuitry-ai/doc-data';
import { pickClaimIntakeYieldAttributes, trackClaimIntakeDifyStream } from './claimIntakeRum';
import { stableStringifyForScoreKey } from './claimScoreFormState';
import {
  normalizeClaimScoreSseEnvelope,
  parseWorkflowFinishedForClaimScore,
  type ClaimScoreWorkflowSseChunk,
  type WorkflowFinishedParsed,
} from './difyWorkflowFinished';
import type { ClaimIntakeSession } from './claimIntakeAgentApi';

/** Drops duplicate pipelines (React StrictMode dev double effects, simultaneous callers). */
const PIPELINE_DEBOUNCE_MS = 550;

/** `user_input.event` for claim-score `dify_stream` (right-rail score). */
export const CLAIM_SCORE_DIFY_EVENT = 'score' as const;

type ContractsHead = {
  contractNumber?: string;
  vehicle?: { vin?: string };
} | undefined;

const AGENTS_STREAM_NOT_CONFIGURED =
  'Agents stream base URL is not configured. Call setAgentsStreamConfig({ baseUrl }) from your app entry with CTY_AGENTS_STREAM_URL.';

export function requireAgentsStreamBaseUrl(raw?: string): string {
  const s = (raw ?? getAgentsStreamBaseUrl()).trim().replace(/\/+$/, '');
  if (!s) throw new Error(AGENTS_STREAM_NOT_CONFIGURED);
  return s;
}

function claimScorePipelineDedupeKey(params: {
  baseUrl?: string;
  formState: Record<string, unknown>;
  contractsData: unknown[];
}): string {
  const base = requireAgentsStreamBaseUrl(params.baseUrl);
  const head = params.contractsData[0] as ContractsHead;
  const cid =
    head && typeof head === 'object'
      ? `${String(head.contractNumber ?? '')}::${String(head.vehicle?.vin ?? '')}`
      : 'noc';
  return `${base}::${cid}::${stableStringifyForScoreKey(params.formState)}`;
}

const lastPipelineKickoffByKey = new Map<string, number>();

export function getClaimScoreAgentsStreamBaseUrl(): string {
  return requireAgentsStreamBaseUrl();
}

function emitSseEnvelopeIfKeyed(
  norm: ClaimScoreWorkflowSseChunk | null,
  onChunk: (chunk: ClaimScoreWorkflowSseChunk) => void,
): void {
  if (norm?.event !== undefined && String(norm.event).trim() !== '') onChunk(norm);
}

function emitNestedJsonStringDataEnvelope(parsedUnknown: unknown, onChunk: (chunk: ClaimScoreWorkflowSseChunk) => void): void {
  const asRec = parsedUnknown != null && typeof parsedUnknown === 'object' ? (parsedUnknown as Record<string, unknown>) : null;
  const nestedStr = typeof asRec?.data === 'string' ? asRec.data.trim() : '';
  if (!nestedStr.startsWith('{')) return;
  try {
    emitSseEnvelopeIfKeyed(normalizeClaimScoreSseEnvelope(JSON.parse(nestedStr)), onChunk);
  } catch {
    /* ignore nested parse errors */
  }
}

function tryParsePayloadLine(payload: string, onChunk: (chunk: ClaimScoreWorkflowSseChunk) => void): void {
  if (!payload.startsWith('{') && !payload.startsWith('[')) return;
  try {
    const parsedUnknown = JSON.parse(payload);
    emitSseEnvelopeIfKeyed(normalizeClaimScoreSseEnvelope(parsedUnknown), onChunk);
    emitNestedJsonStringDataEnvelope(parsedUnknown, onChunk);
  } catch {
    /* malformed line */
  }
}

function parseDifySseOrNdjsonPayloadBody(buffer: string, onChunk: (chunk: ClaimScoreWorkflowSseChunk) => void): void {
  const lines = buffer.split(/\r?\n/u);
  for (const line of lines) {
    let payload = line.trim();
    if (!payload || payload.startsWith(':')) continue;
    if (payload === '[DONE]') continue;
    if (payload.startsWith('data:')) payload = payload.slice('data:'.length).trim();
    tryParsePayloadLine(payload, onChunk);
  }
}

function extractLastWorkflowFinishedFromDifyRawBody(rawBody: string): WorkflowFinishedParsed | null {
  let lastWorkflow: WorkflowFinishedParsed | null = null;
  parseDifySseOrNdjsonPayloadBody(rawBody, (c) => {
    const wf = parseWorkflowFinishedForClaimScore(c);
    if (wf != null) lastWorkflow = wf;
  });
  return lastWorkflow;
}

export type ClaimScoreDifyStreamResult = Readonly<{ workflowFinished: WorkflowFinishedParsed | null }>;

export type ClaimScorePipelineResult =
  | Readonly<{ skipped: true }>
  | Readonly<{ skipped: false; dify: ClaimScoreDifyStreamResult }>;

/**
 * POST /dify_stream for the score event.
 * Uses the same aiworkerId + sessionId that all other claim-intake events (VIN, estimateparser,
 * 3csmapper, saveclaim) already use — no extra /start-stream call required.
 */
async function claimScoreDifyStreamUntracked(params: {
  token: string;
  baseUrl?: string;
  session: ClaimIntakeSession;
  formState: Record<string, unknown>;
  contractsData: unknown[];
  dummyClaimId?: string | null;
  finalSubmission?: boolean;
  claimNumber?: string | null;
  aiworkerUsageId?: string;
  signal?: AbortSignal;
}): Promise<ClaimScoreDifyStreamResult> {
  const base = requireAgentsStreamBaseUrl(params.baseUrl);
  const url = `${base}/dify_stream`;

  const claimNumTrimmed =
    params.claimNumber != null && typeof params.claimNumber === 'string'
      ? params.claimNumber.trim()
      : '';

  const user_input: Record<string, unknown> = {
    event: CLAIM_SCORE_DIFY_EVENT,
    form_state: params.formState,
    contracts_data: params.contractsData,
    ...(params.dummyClaimId ? { dummy_claim_id: params.dummyClaimId } : {}),
    ...(params.finalSubmission ? { final_submission: true } : {}),
    ...(claimNumTrimmed ? { claim_number: claimNumTrimmed } : {}),
    ...(params.aiworkerUsageId ? { aiworker_usage_id: params.aiworkerUsageId } : {}),
  };

  const body = {
    payload: {
      aiworker_id: params.session.aiworkerId,
      session_id: params.session.sessionId,
      user_input,
    },
  };

  const bearer = await getValidClaimIntakeBearerToken(params.token);

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'text/event-stream',
      Authorization: `Bearer ${bearer}`,
    },
    body: JSON.stringify(body),
    signal: params.signal,
  });

  if (!response.ok) {
    const errText = await response.text().catch(() => '');
    throw new Error(`dify_stream failed: ${response.status} ${errText}`);
  }

  if (params.signal?.aborted) {
    throw new DOMException('Aborted', 'AbortError');
  }

  const rawBody = await response.text();

  let workflowFinished = extractLastWorkflowFinishedFromDifyRawBody(rawBody);
  const trimmedHead = rawBody.trim();
  if (workflowFinished === null && trimmedHead.startsWith('{')) {
    try {
      const once = normalizeClaimScoreSseEnvelope(JSON.parse(trimmedHead));
      if (once?.event !== undefined) {
        workflowFinished = parseWorkflowFinishedForClaimScore(once);
      }
    } catch {
      // ignore
    }
  }

  return { workflowFinished };
}

export async function claimScoreDifyStream(params: {
  token: string;
  baseUrl?: string;
  session: ClaimIntakeSession;
  formState: Record<string, unknown>;
  contractsData: unknown[];
  dummyClaimId?: string | null;
  finalSubmission?: boolean;
  claimNumber?: string | null;
  /** Chains score with VIN / estimateparser / 3csmapper / saveclaim under one usage log. */
  aiworkerUsageId?: string;
  signal?: AbortSignal;
}): Promise<ClaimScoreDifyStreamResult> {
  return trackClaimIntakeDifyStream({
    session: params.session,
    event: CLAIM_SCORE_DIFY_EVENT,
    aiworkerUsageId: params.aiworkerUsageId,
    extraStartAttributes: {
      claim_number: params.claimNumber ?? null,
      has_dummy_claim_id: Boolean(params.dummyClaimId),
      final_submission: Boolean(params.finalSubmission),
    },
    run: () => claimScoreDifyStreamUntracked(params),
    completeAttributes: (result) => {
      const wf = result.workflowFinished;
      const outputs = wf?.outputsSnapshot;
      const usageFromOutputs =
        typeof outputs?.aiworker_usage_id === 'string' ? outputs.aiworker_usage_id : null;
      const dummyFromOutputs =
        typeof outputs?.dummy_claim_id === 'string' ? outputs.dummy_claim_id : null;
      const messageFromOutputs =
        typeof outputs?.message_id === 'string' ? outputs.message_id : null;
      return pickClaimIntakeYieldAttributes({
        aiworkerUsageId: usageFromOutputs ?? params.aiworkerUsageId,
        dummyClaimId: dummyFromOutputs ?? params.dummyClaimId,
        claimNumber: params.claimNumber,
        messageId: messageFromOutputs,
        hasWorkflowOutput: wf != null,
      });
    },
  });
}

export async function runClaimScoreDebugPipeline(params: {
  token: string;
  baseUrl?: string;
  /** Existing claim-intake session (same aiworkerId/sessionId used by VIN, 3csmapper, etc.). */
  session?: ClaimIntakeSession | null;
  formState: Record<string, unknown>;
  contractsData: unknown[];
  dummyClaimId?: string | null;
  finalSubmission?: boolean;
  claimNumber?: string | null;
  aiworkerUsageId?: string;
  skipDedupe?: boolean;
  signal?: AbortSignal;
}): Promise<ClaimScorePipelineResult> {
  if (!params.session?.aiworkerId || !params.session?.sessionId) {
    return { skipped: true };
  }

  if (!params.skipDedupe) {
    const dedupeKey = claimScorePipelineDedupeKey({
      baseUrl: params.baseUrl,
      formState: params.formState,
      contractsData: params.contractsData,
    });
    const now = Date.now();
    const lastKick = lastPipelineKickoffByKey.get(dedupeKey);
    if (lastKick !== undefined && now - lastKick < PIPELINE_DEBOUNCE_MS) {
      return { skipped: true };
    }
    lastPipelineKickoffByKey.set(dedupeKey, now);
  }

  const dify = await claimScoreDifyStream({
    token: params.token,
    baseUrl: params.baseUrl,
    session: params.session,
    formState: params.formState,
    contractsData: params.contractsData,
    dummyClaimId: params.dummyClaimId,
    finalSubmission: params.finalSubmission,
    claimNumber: params.claimNumber,
    aiworkerUsageId: params.aiworkerUsageId,
    signal: params.signal,
  });
  return { skipped: false, dify };
}